import { D as DialogExt } from './dialog-DbywEvhz.js';
import { C as Cart } from './cart-BPgrnoRc.js';
import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './focus-trap.esm-148dAOfA.js';
import './notyf-DIw0RvBj.js';
import './uniq-D7II7obF.js';

const COOKIE_NAME = 'cart_reminder';
/** Suppress reminder until a new browser session (tab session ends). Set when cart activity arms the cookie. */
const SESSION_SUPPRESS_KEY = 'cart_reminder:suppress_same_session';

class CartReminder extends DialogExt {
  #cookieDuration = parseInt(this.dataset.cookieDuration || '30');
  #frequency = parseInt(this.dataset.frequency || '1');
  #subtitle = this.dataset.subtitle || '';
  #subtitleEl = this.querySelector('[data-subtitle]');
  #unsubscribers = [];

  connectedCallback() {
    this.#unsubscribers.push(
      PubSub.subscribe('cart:add', ({ items }) => {
        if (items?.length) this.#setCookie();
      }),
      ...['cart:change', 'cart:update'].map((event) =>
        PubSub.subscribe(event, ({ item_count }) => {
          item_count ? this.#setCookie() : this.#deleteCookie();
        })
      ),
      PubSub.subscribe('cart:get', (cart) => this.#handleCartState(cart)),
      PubSub.subscribe('cart:clear', () => {
        this.#deleteCookie();
        this.close();
      })
    );

    Cart.get();
  }

  disconnectedCallback() {
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Show the reminder popup and update the subtitle with the current item count
   * @param {number} itemCount
   */
  open(itemCount) {
    if (this.#subtitleEl) {
      this.#subtitleEl.textContent = this.#subtitle.replace('{{count}}', itemCount);
    }
    super.open();
    this.#markAsShown();
  }

  /**
   * Show or dismiss the reminder based on the current cart state
   * @param {{ item_count: number }} cart
   */
  #handleCartState(cart) {
    if (!cart.item_count) {
      this.#deleteCookie();
      this.close();
      return;
    }

    if (!this.#getCookie()) return;
    if (this.#isCartPage()) return;
    if (!this.#shouldShow()) return;

    this.open(cart.item_count);
  }

  #isCartPage() {
    return window.location.pathname.startsWith('/cart');
  }

  /**
   * Whether enough days have passed since the reminder was last shown
   * @returns {boolean}
   */
  #shouldShow() {
    const cookie = this.#getCookie();
    if (!cookie) return false;

    // Do not show in the same browser session as cart activity; next session may show.
    if (this.#isSuppressedForSession()) return false;

    const lastShown = cookie.lastShown ? new Date(cookie.lastShown) : null;
    if (!lastShown) return true;

    const daysSinceShown = (Date.now() - lastShown.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceShown >= this.#frequency;
  }

  #markAsShown() {
    this.#saveCookie({ ...this.#getCookie(), lastShown: new Date().toISOString() });
  }

  #setCookie() {
    this.#armSessionSuppress();
    this.#saveCookie({
      ...this.#getCookie(),
      addedAt: new Date().toISOString(),
    });
  }

  #armSessionSuppress() {
    try {
      sessionStorage.setItem(SESSION_SUPPRESS_KEY, '1');
    } catch {
      // sessionStorage unavailable (e.g. private mode)
    }
  }

  #isSuppressedForSession() {
    try {
      return sessionStorage.getItem(SESSION_SUPPRESS_KEY) === '1';
    } catch {
      return false;
    }
  }

  /**
   * Write cookie data with the configured expiry duration
   * @param {object} data
   */
  #saveCookie(data) {
    const expires = new Date();
    expires.setDate(expires.getDate() + this.#cookieDuration);
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }

  #getCookie() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch {
      return null;
    }
  }

  #deleteCookie() {
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

customElements.define('cart-reminder', CartReminder);
